import { DatePipe } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AuthenticationService } from '@services/authentication';
import { CacheService } from '@services/cache';
import { DataLayerService } from '@services/data-layer';
import { LogsService } from '@services/logs';
import { ButtonComponent } from '@shared/components/button';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';

@Component({
  selector: 'app-form-layout-1-success',
  imports: [TitleComponent, DatePipe, ButtonComponent],
  templateUrl: './form-layout-1-success.component.html',
  styleUrl: './form-layout-1-success.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormLayout1SuccessComponent implements OnInit, OnDestroy {
  readonly title = signal<Title>({
    level: 'H1',
    text: 'Votre rendez-vous est confirmé !',
    size: {
      mobile: 'H1',
      desktop: 'H1',
    },
    color: 'WHITE',
  });
  store = signal<string>(null!);
  date = signal<Date>(null!);
  dataLoaded = signal(false);
  /** Unknown until the auth check answers, so the wrong buttons never show for a moment. */
  readonly showAccountPush = signal<boolean | undefined>(undefined);
  #cacheService = inject(CacheService);
  #router = inject(Router);
  readonly #authenticationService = inject(AuthenticationService);
  readonly #logsService = inject(LogsService);
  readonly #dataLayerService = inject(DataLayerService);

  constructor() {
    afterNextRender(() => {
      this.#authenticationService
        .isAuthenticated()
        .then((isAuthenticated) =>
          this.showAccountPush.set(this.#authenticationService.isAvailable && !isAuthenticated)
        )
        .catch((error: unknown) => {
          this.#logsService.error('FormLayout1SuccessComponent', 'Failed to read the Auth0 session', error);
          this.showAccountPush.set(false);
        });
    });
  }

  ngOnInit(): void {
    void this.#cacheService
      .get('form-layout-1')
      .then((dataCache) => {
        if (dataCache === null) {
          this.redirectToForm();
        } else {
          const data: { store: string; date: Date } = dataCache as { store: string; date: Date };
          this.store.set(data.store ?? '');
          this.date.set(data.date ?? undefined);
          this.dataLoaded.set(true);
        }
      })
      .catch((error: unknown) => {
        this.#logsService.error('FormLayout1SuccessComponent', 'Failed to read appointment recap from cache', error);
        this.redirectToForm();
      });
  }

  ngOnDestroy(): void {
    void this.#cacheService.remove('form-layout-1');
  }

  login(signup: boolean): void {
    if (signup) {
      this.#dataLayerService.logEspaceClientEvent({
        event: 'rdv_creer_compte_cta',
        event_localisation: 'tunnel rdv',
        event_type: 'click',
      });
    }
    this.#authenticationService.loginWithRedirect(environment.ESPACE_CLIENT.url, signup).catch((error: unknown) => {
      this.#logsService.error('FormLayout1SuccessComponent', 'Failed to start the Auth0 login', error);
    });
  }

  private redirectToForm() {
    const urlSegments = this.#router.url.split('/');
    const newUrl = `/${urlSegments[1]}`;
    void this.#router.navigateByUrl(newUrl);
  }
}
